import { RESPONSE_TYPES, resolveResponseType } from '../utils/responseTypes';
const SESSION_KEY = 'woosah_first_conversation_session_v1';

function readSession() {
  try {
    const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    if (!value?.token || Date.parse(value.expiresAt) <= Date.now()) return null;
    return value;
  } catch {
    return null;
  }
}

function writeSession(token, expiresAt) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ token, expiresAt }));
  } catch {
    // The server token remains in memory when storage is unavailable.
  }
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Best effort only.
  }
}

function attributionFromPage(variant) {
  const params = new URLSearchParams(window.location.search);
  const fields = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'landing_variant',
  ];
  const attribution = {};
  fields.forEach(field => {
    const value = params.get(field);
    if (value) attribution[field] = value.slice(0, 200);
  });
  attribution.landing_path = window.location.pathname.slice(0, 200);
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      attribution.referrer = `${referrer.origin}${referrer.pathname}`.slice(0, 200);
    } catch {
      // Ignore malformed referrers.
    }
  }
  if (variant) attribution.landing_variant = variant.slice(0, 200);
  return attribution;
}

function deviceCategory() {
  if (window.matchMedia('(max-width: 640px)').matches) return 'mobile';
  if (window.matchMedia('(max-width: 1024px)').matches) return 'tablet';
  return 'desktop';
}

const styles = `
  :host { display:block; color:#17352b; font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; }
  * { box-sizing:border-box; }
  .shell { width:100%; max-width:900px; margin:0 auto; border:1px solid #d9e8e1; border-radius:20px; background:#fff; box-shadow:0 14px 38px rgba(24,74,56,.1); overflow:hidden; }
  .intro { padding:20px clamp(18px,4vw,30px) 16px; background:linear-gradient(180deg,#f3fbf7 0%,#fff 100%); }
  .intro-row { display:flex; align-items:center; justify-content:space-between; gap:20px; }
  .intro-copy { min-width:0; text-align:left; }
  h2 { margin:0; color:#17352b; font-size:clamp(27px,4vw,34px); line-height:1.08; letter-spacing:-.025em; }
  .sub { margin:6px 0 0; max-width:560px; color:#526c62; font-size:15px; line-height:1.4; }
  .account-cta { flex:0 0 auto; min-height:42px; padding:0 16px; border:1.5px solid #17835f; color:#14694e; background:#fff; white-space:nowrap; }
  .account-cta:hover { color:#fff; background:#17835f; }
  .messages { display:none; min-height:112px; max-height:min(38vh,300px); overflow-y:auto; padding:14px clamp(14px,4vw,24px) 6px; overscroll-behavior:contain; scroll-behavior:smooth; }
  .messages.active { display:flex; flex-direction:column; gap:9px; }
  .message { max-width:82%; padding:9px 13px; border-radius:16px; white-space:pre-wrap; overflow-wrap:anywhere; font-size:15px; line-height:1.4; }
  .message.user { margin-left:auto; color:#fff; background:#17835f; border-bottom-right-radius:6px; }
  .message.assistant { margin-right:auto; color:#243d34; background:#eef7f3; border-bottom-left-radius:6px; }
  .composer { padding:11px clamp(14px,4vw,24px) max(13px,env(safe-area-inset-bottom)); border-top:1px solid #e7f0ec; background:#fff; }
  .input-wrap { display:flex; align-items:flex-end; gap:9px; }
  textarea { width:100%; min-height:48px; max-height:108px; resize:none; border:1.5px solid #bcd4ca; border-radius:14px; padding:12px 13px; color:#17352b; background:#fff; font:inherit; font-size:16px; line-height:1.35; outline:none; }
  textarea:focus { border-color:#17835f; box-shadow:0 0 0 3px rgba(23,131,95,.14); }
  textarea:disabled { background:#f1f5f3; color:#74877f; cursor:not-allowed; }
  button,.cta { min-height:46px; border:0; border-radius:13px; padding:0 17px; font:inherit; font-weight:700; cursor:pointer; touch-action:manipulation; }
  .send { flex:0 0 auto; color:#fff; background:#17835f; }
  .send:hover { background:#116d4e; }
  button:focus-visible,.cta:focus-visible { outline:3px solid rgba(23,131,95,.3); outline-offset:2px; }
  button:disabled { opacity:.55; cursor:not-allowed; }
  .status { min-height:18px; margin:6px 2px 0; color:#5f756c; font-size:12.5px; line-height:1.35; }
  .status:empty { display:none; }
  .typing { display:inline-flex; gap:4px; align-items:center; }
  .dot { width:6px; height:6px; border-radius:50%; background:#17835f; animation:pulse 1.2s infinite ease-in-out; }
  .dot:nth-child(2) { animation-delay:.15s; }.dot:nth-child(3) { animation-delay:.3s; }
  @keyframes pulse { 0%,80%,100%{opacity:.28;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }
  .gate { display:none; margin:10px clamp(14px,4vw,24px) 3px; padding:14px; border-radius:16px; background:#eff9f5; border:1px solid #c7e4d8; text-align:center; }
  .gate.active { display:block; }
  .gate h3 { margin:0; color:#17352b; font-size:20px; }
  .gate p { margin:7px auto 14px; color:#526c62; line-height:1.45; }
  .cta { display:inline-flex; align-items:center; justify-content:center; color:#fff; background:#17835f; text-decoration:none; }
  .privacy { margin:5px 2px 0; color:#71857d; font-size:11.5px; line-height:1.35; text-align:center; }
  .privacy a { color:#496d60; font-weight:700; }
  .retry { min-height:40px; margin-top:8px; padding:0 13px; color:#14694e; background:#e7f5ef; }
  .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  @media (max-width:620px) {
    .intro-row { align-items:flex-start; gap:12px; }
    h2 { font-size:clamp(25px,7vw,30px); }
    .account-cta { min-height:38px; padding:0 12px; font-size:13px; }
    .messages { min-height:180px; max-height:360px; max-height:min(48svh,360px); padding-top:16px; }
    .message { max-width:92%; padding:11px 14px; }
    .composer { padding:13px 14px max(15px,env(safe-area-inset-bottom)); }
    textarea { min-height:58px; max-height:132px; }
  }
  @media (max-width:440px) {
    .shell { border-radius:16px; }
    .intro-row { display:block; }
    .account-cta { margin-top:11px; }
    .messages { min-height:220px; max-height:360px; max-height:min(50svh,360px); }
    .input-wrap { display:grid; grid-template-columns:minmax(0,1fr); gap:8px; }
    textarea { min-height:72px; max-height:144px; }
    .send { width:100%; min-height:50px; padding:0 13px; }
    .privacy { text-align:left; }
  }
  @media (prefers-reduced-motion:reduce) { .messages{scroll-behavior:auto}.dot{animation:none;opacity:.75} }
`;

export function mountFirstConversation(container, options = {}) {
  if (!container || container.dataset.woosahMounted === 'true') return null;
  container.dataset.woosahMounted = 'true';
  const shadow = container.attachShadow ? container.attachShadow({ mode: 'open' }) : container;
  const apiBase = (
    options.apiBase ||
    container.dataset.apiBase ||
    import.meta.env.VITE_API_BASE_URL ||
    'https://app.getwoosah.com'
  ).replace(/\/$/, '');
  const signupUrl = options.signupUrl || container.dataset.signupUrl || `${apiBase}/signup`;
  const variant = options.variant || container.dataset.variant || 'homepage_default';
  const fetchImpl = options.fetchImpl || window.fetch.bind(window);
  const navigate = options.navigate || (destination => window.location.assign(destination));

  shadow.innerHTML = `
    <style>${styles}</style>
    <section class="shell" aria-labelledby="woosah-chat-title">
      <div class="intro">
        <div class="intro-row">
          <div class="intro-copy">
            <h2 id="woosah-chat-title">What's on your mind?</h2>
            <p class="sub">Tell Woosah what's going on. No account needed to start.</p>
          </div>
          <button class="account-cta signup-cta" type="button">Create free account</button>
        </div>
      </div>
      <div style="padding:0 24px 14px"><label for="woosah-response-type">Response type</label>
        <select id="woosah-response-type" style="display:block;width:100%;margin-top:6px;padding:10px;border:1px solid #bcd4ca;border-radius:10px;background:white;font:inherit;color:#17352b">${RESPONSE_TYPES.map(type => `<option value="${type.id}"${type.id === 'balanced' ? ' selected' : ''}>${type.label}</option>`).join('')}</select>
      </div>
      <div class="messages" role="log" aria-live="polite" aria-relevant="additions"></div>
      <div class="gate" role="region" aria-label="Continue with Woosah">
        <h3>Want to keep talking?</h3>
        <p>Create a free Woosah account to continue this conversation and come back to it later.</p>
        <button class="cta gate-cta signup-cta" type="button">Continue with Woosah Free</button>
      </div>
      <form class="composer">
        <label class="sr-only" for="woosah-first-message">Tell Woosah what's going on</label>
        <div class="input-wrap">
          <textarea id="woosah-first-message" rows="2" placeholder="Tell Woosah what's going on..." enterkeyhint="send" required></textarea>
          <button class="send" type="submit">Talk to Woosah</button>
        </div>
        <div class="status" role="status" aria-live="polite">Preparing your private conversation…</div>
        <p class="privacy"><span class="privacy-summary">Messages are encrypted temporarily and sent to our server for a reply.</span> <a href="https://getwoosah.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy details</a>.</p>
      </form>
    </section>`;

  const elements = {
    form: shadow.querySelector('form'),
    responseType: shadow.querySelector('#woosah-response-type'),
    textarea: shadow.querySelector('textarea'),
    send: shadow.querySelector('.send'),
    messages: shadow.querySelector('.messages'),
    status: shadow.querySelector('.status'),
    gate: shadow.querySelector('.gate'),
    signupButtons: [...shadow.querySelectorAll('.signup-cta')],
    privacy: shadow.querySelector('.privacy'),
    privacySummary: shadow.querySelector('.privacy-summary'),
  };
  let session = readSession();
  let loading = false;
  let startedTracked = false;
  let retryMessage = null;
  let messageCount = 0;

  function applySessionConfiguration(data) {
    if (data?.configuration?.response_type) {
      elements.responseType.value = resolveResponseType(data.configuration.response_type).id;
      savedResponseType = elements.responseType.value;
    }
    if (Number.isInteger(data?.max_message_characters)) {
      elements.textarea.maxLength = data.max_message_characters;
    }
    if (Number.isInteger(data?.ttl_minutes)) {
      elements.privacySummary.textContent = `Messages are encrypted temporarily, sent to our server for a reply, and expire after ${data.ttl_minutes} minutes if you don't create an account.`;
    }
  }

  async function api(path, init = {}, anonymousToken = session?.token) {
    const headers = {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    };
    if (anonymousToken) headers['X-Woosah-Anonymous-Token'] = anonymousToken;
    const response = await fetchImpl(`${apiBase}/api/anonymous-chat${path}`, {
      ...init,
      headers,
      credentials: 'omit',
    });
    const data = response.status === 204 ? null : await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data?.message || "Woosah couldn't respond just now.");
      error.status = response.status;
      error.code = data?.error;
      error.data = data;
      throw error;
    }
    return data;
  }

  function track(eventName, eventData = {}) {
    if (!session?.token) return;
    api('/events', {
      method: 'POST',
      keepalive: true,
      body: JSON.stringify({
        event_name: eventName,
        event_data: {
          device_category: deviceCategory(),
          experiment_variant: variant,
          ...eventData,
        },
      }),
    }).catch(() => undefined);
  }

  function addMessage(message) {
    const bubble = document.createElement('div');
    bubble.className = `message ${message.role === 'user' ? 'user' : 'assistant'}`;
    bubble.textContent = message.content;
    elements.messages.classList.add('active');
    elements.messages.appendChild(bubble);
    elements.messages.scrollTop = elements.messages.scrollHeight;
  }

  function setLoading(value) {
    loading = value;
    elements.send.disabled = value || !session?.token;
    elements.responseType.disabled = value;
    elements.textarea.disabled = value || !session?.token;
    if (value) {
      elements.status.innerHTML =
        '<span class="typing" aria-label="Woosah is responding"><span class="dot"></span><span class="dot"></span><span class="dot"></span> Woosah is responding</span>';
    }
  }

  function showGate() {
    elements.gate.classList.add('active');
    elements.status.textContent = 'You can still send an urgent safety message here.';
  }

  function showRetry(message) {
    retryMessage = message;
    elements.status.textContent = "Woosah couldn't respond just now. ";
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'retry';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => sendMessage(retryMessage, false));
    elements.status.appendChild(retry);
  }

  async function createOrRestoreSession() {
    elements.send.disabled = true;
    elements.textarea.disabled = true;

    if (session) {
      try {
        const restored = await api('/session', { method: 'GET' });
        applySessionConfiguration(restored);
        (restored.messages || []).forEach(addMessage);
        messageCount = (restored.messages || []).filter(message => message.role === 'user').length;
        if (restored.limit_reached) showGate();
        elements.status.textContent = 'Your private conversation is ready.';
        elements.send.textContent = messageCount ? 'Send' : 'Talk to Woosah';
        elements.send.disabled = false;
        elements.textarea.disabled = false;

        return;
      } catch {
        clearSession();
        session = null;
      }
    }

    try {
      const created = await api(
        '/session',
        {
          method: 'POST',
          body: JSON.stringify({
            attribution: attributionFromPage(variant),
            response_type: elements.responseType.value,
          }),
        },
        null
      );
      session = { token: created.session_token, expiresAt: created.expires_at };
      applySessionConfiguration(created);
      writeSession(session.token, session.expiresAt);
      elements.status.textContent = 'Your private conversation is ready.';
      elements.send.disabled = false;
      elements.textarea.disabled = false;

      track('homepage_chat_viewed');
    } catch (error) {
      elements.status.textContent = error.message;
    }
  }

  async function sendMessage(text, renderUser = true) {
    const value = String(text || '').trim();
    if (!value || loading || !session?.token) return;
    if (!startedTracked) {
      startedTracked = true;
      track('homepage_chat_started');
    }
    if (renderUser) {
      addMessage({ role: 'user', content: value });
      messageCount += 1;
    }
    retryMessage = null;
    elements.textarea.value = '';
    elements.send.textContent = 'Send';
    elements.gate.classList.remove('active');
    setLoading(true);
    try {
      const result = await api('/message', {
        method: 'POST',
        body: JSON.stringify({
          message: value,
          response_type: elements.responseType.value,
        }),
      });
      addMessage(result.reply);
      if (result.crisis_detected) {
        elements.gate.classList.remove('active');
        elements.status.textContent =
          'Crisis resources will stay visible. You do not need an account for safety support.';
      } else if (result.limit_reached) {
        showGate();
      } else if (result.remaining_ai_responses <= 2) {
        elements.status.textContent = `${result.remaining_ai_responses} free repl${result.remaining_ai_responses === 1 ? 'y' : 'ies'} remaining.`;
      } else {
        elements.status.textContent = '';
      }
      requestAnimationFrame(() => elements.textarea.focus({ preventScroll: true }));
    } catch (error) {
      if (error.code === 'anonymous_limit_reached') showGate();
      else if (error.code === 'session_expired') {
        clearSession();
        session = null;
        elements.status.textContent = error.message;
      } else showRetry(value);
    } finally {
      setLoading(false);
    }
  }

  let savedResponseType = 'balanced';
  elements.responseType.addEventListener('change', async () => {
    if (!session?.token) {
      savedResponseType = elements.responseType.value;
      return;
    }
    setLoading(true);
    try {
      const result = await api('/response-type', {
        method: 'POST',
        body: JSON.stringify({ response_type: elements.responseType.value }),
      });
      savedResponseType = result.response_type;
      elements.status.textContent = '';
    } catch (error) {
      elements.responseType.value = savedResponseType;
      elements.status.textContent = error.message;
    } finally {
      setLoading(false);
    }
  });

  elements.form.addEventListener('submit', event => {
    event.preventDefault();
    sendMessage(elements.textarea.value);
  });
  elements.textarea.addEventListener('keydown', event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      elements.form.requestSubmit();
    }
  });
  elements.textarea.addEventListener('focus', () => {
    if (!startedTracked) {
      startedTracked = true;
      track('homepage_chat_started');
    }
  });
  elements.textarea.addEventListener('input', () => {
    elements.textarea.style.height = 'auto';
    const configuredMaxHeight = Number.parseFloat(
      window.getComputedStyle(elements.textarea).maxHeight
    );
    const maxHeight = Number.isFinite(configuredMaxHeight) ? configuredMaxHeight : 108;
    elements.textarea.style.height = `${Math.min(elements.textarea.scrollHeight, maxHeight)}px`;
  });
  function directSignupDestination() {
    const destination = new URL(signupUrl, window.location.href);
    destination.searchParams.set('response_type', elements.responseType.value);
    Object.entries(attributionFromPage(variant)).forEach(([key, value]) => {
      if (key.startsWith('utm_') || ['gclid', 'landing_path', 'landing_variant'].includes(key)) {
        destination.searchParams.set(key, value);
      }
    });
    return destination;
  }

  async function startSignup(clickedButton) {
    const destination = directSignupDestination();
    track('homepage_signup_clicked', { message_number: messageCount });
    // Account creation does not require an anonymous session or transcript.
    if (!session?.token || messageCount === 0) {
      navigate(destination.toString());
      return;
    }

    const labels = elements.signupButtons.map(button => button.textContent);
    elements.signupButtons.forEach(button => {
      button.disabled = true;
    });
    clickedButton.textContent = 'Preparing signup…';
    let timer;
    try {
      const handoff = await Promise.race([
        api('/handoff', { method: 'POST', body: '{}' }),
        new Promise((_, reject) => {
          timer = setTimeout(
            () => reject(new Error('Conversation transfer timed out.')),
            options.handoffTimeoutMs || 8000
          );
        }),
      ]);
      if (!handoff?.handoff_token) throw new Error('Conversation transfer is unavailable.');
      destination.searchParams.set('handoff', handoff.handoff_token);
      Object.entries(handoff.attribution || {}).forEach(([key, value]) => {
        if (key.startsWith('utm_') || ['gclid', 'landing_path', 'landing_variant'].includes(key)) {
          destination.searchParams.set(key, value);
        }
      });
      navigate(destination.toString());
    } catch {
      // Keep the transcript here and offer an explicit choice rather than
      // silently discarding it or leaving account creation blocked.
      elements.status.textContent = 'Your conversation could not be transferred. You can retry or ';
      const fallback = document.createElement('a');
      fallback.href = directSignupDestination().toString();
      fallback.textContent = 'create an account without transferring this conversation';
      elements.status.appendChild(fallback);
      elements.signupButtons.forEach((button, index) => {
        button.disabled = false;
        button.textContent = labels[index];
      });
    } finally {
      clearTimeout(timer);
    }
  }

  elements.signupButtons.forEach(button => {
    button.addEventListener('click', () => startSignup(button));
  });

  createOrRestoreSession();
  return { shadowRoot: shadow, sendMessage };
}

export function decorateSignupLinks() {
  const attribution = attributionFromPage('homepage_default');
  document.querySelectorAll('a[href]').forEach(link => {
    try {
      const destination = new URL(link.href, window.location.href);
      const isSignup =
        destination.origin === 'https://app.getwoosah.com' && destination.pathname === '/signup';
      const isMarketingPage =
        destination.origin === window.location.origin &&
        ['https://getwoosah.com', 'https://www.getwoosah.com'].includes(destination.origin) &&
        ['/pricing', '/features'].includes(destination.pathname.replace(/\/$/, ''));
      if (!isSignup && !isMarketingPage) return;
      if (destination.pathname === window.location.pathname && destination.hash) return;
      Object.entries(attribution).forEach(([key, value]) => {
        if (key.startsWith('utm_') || ['gclid', 'landing_path', 'landing_variant'].includes(key)) {
          destination.searchParams.set(key, value);
        }
      });
      link.href = destination.toString();
    } catch {
      // Ignore malformed unrelated links.
    }
  });
}

function autoMount() {
  decorateSignupLinks();
  const containers = [
    ...document.querySelectorAll('[data-woosah-first-conversation]'),
    document.getElementById('woosah-first-conversation'),
  ].filter(Boolean);
  [...new Set(containers)].forEach(container => mountFirstConversation(container));
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMount);
  else autoMount();
}
